// Library at /o/:slug/library (reset PR2; Home took over /o/:slug). The DS LibraryScreen: a
// title + count header with the scope as a ViewToggle (SegmentedControl) over the RecordingCard
// grid, cursor-paginated and scoped to one org:
// - Mine / All are §4 #19's filter=mine|all.
// - Shared with me (FR-L4a) is filter=shared on the server: recordings granted to my user, my
//   verified email or my teams, never my own. List rows carry no grant info, so the client
//   could not derive it from filter=all.
// The filter and the search text live in the URL (?filter=…&q=…) so a library view is a link.
//
// Issue #4: search (client-side, over fetched results, by title and owner) and sort run on the
// client until F2 ships the search backend. Issue #5: infinite scroll via IntersectionObserver.
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, Navigate, useParams, useSearchParams } from "react-router-dom";
import { Badge, Button, Callout, Icon, SegmentedControl, Select } from "@grackle/ui";
import { api, ApiFail } from "../api/client";
import type { GrackleDetail, GrackleOut, PagedResponse } from "../api/types";
import { AppShell } from "../components/AppShell";
import { PageLoader, EmptyState } from "../components/ui";
import { useSession } from "../session";
import { cardPageStyle } from "./cardColumns";
import GrackleCard from "./GrackleCard";
import { DEFAULT_LIBRARY_FILTER, parseLibraryFilter, uploadPath, type LibraryFilter } from "./routes";
import { useIsMobileLayout } from "../lib/mobileLayout";
import "./library.css";

type SortOption = "newest" | "oldest" | "name-asc" | "name-desc" | "duration-asc" | "duration-desc";

const SORT_LABELS: Record<SortOption, string> = {
  newest: "Newest first",
  oldest: "Oldest first",
  "name-asc": "Name A to Z",
  "name-desc": "Name Z to A",
  "duration-asc": "Shortest first",
  "duration-desc": "Longest first",
};

const SCOPE_OPTIONS: { value: LibraryFilter; label: string }[] = [
  { value: "mine", label: "Mine" },
  { value: "all", label: "All" },
  { value: "shared", label: "Shared with me" },
];

/** The DS prototype titles the screen "My Library" for Mine and All (sentence case here, per
 * the content rules); the shared scope keeps the sidebar entry's name. */
const FILTER_TITLES: Record<LibraryFilter, string> = {
  mine: "My library",
  all: "My library",
  shared: "Shared with me",
};

function countLabel(n: number, noun = "recording"): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

function SkeletonGrid() {
  return (
    <div className="lib-grid" aria-busy="true">
      {Array.from({ length: 8 }).map((_, i) => (
        <div className="lib-skeleton-card" key={i}>
          <div className="lib-skeleton-thumb" />
          <div className="lib-skeleton-line" style={{ width: "70%" }} />
          <div className="lib-skeleton-line" style={{ width: "40%" }} />
        </div>
      ))}
    </div>
  );
}

export default function LibraryPage() {
  const { slug } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user, orgs, loading: sessionLoading } = useSession();
  const org = orgs.find((o) => o.slug === slug);

  const filter = parseLibraryFilter(searchParams.get("filter"));
  const searchQuery = (searchParams.get("q") ?? "").trim();
  const [sortBy, setSortBy] = useState<SortOption>("newest");
  const [allItems, setAllItems] = useState<GrackleOut[]>([]); // unfiltered/un-sorted master list
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [reloadTick, setReloadTick] = useState(0);

  const observerTarget = useRef<HTMLDivElement>(null);

  function setFilter(next: LibraryFilter) {
    const params = new URLSearchParams(searchParams);
    if (next === DEFAULT_LIBRARY_FILTER) params.delete("filter");
    else params.set("filter", next);
    setSearchParams(params);
  }

  // Issue #5: IntersectionObserver for infinite scroll
  useEffect(() => {
    if (!observerTarget.current || !nextCursor || loadingMore || loading) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && !loadingMore && nextCursor) {
          loadMore();
        }
      },
      { rootMargin: "200px" },
    );
    observer.observe(observerTarget.current);
    return () => observer.disconnect();
  }, [nextCursor, loadingMore, loading]);

  // Fetch a page of results. Every filter, shared included, uses the same cursor/limit paging.
  const fetchPage = useCallback(async (orgId: string, f: LibraryFilter, cursor?: string | null) => {
    const params = new URLSearchParams({ filter: f, limit: "24" });
    if (cursor) params.set("cursor", cursor);
    return api<PagedResponse<GrackleOut>>("GET", `/orgs/${orgId}/grackles?${params.toString()}`);
  }, []);

  // Reset + first page whenever the org, filter, or a manual retry changes. Later pages are
  // loadMore()'s job — keeping the cursor out of this effect's deps is what prevents the
  // reset/append feedback loop that previously double-fetched (or never fetched) pages.
  useEffect(() => {
    if (!org) return;
    let cancelled = false;
    setAllItems([]);
    setNextCursor(null);
    setError(null);
    setLoading(true);
    fetchPage(org.id, filter, null)
      .then((res) => {
        if (cancelled) return;
        setAllItems(res.items);
        setNextCursor(res.next_cursor);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err instanceof ApiFail ? err.message : "Couldn't reach Grackle. Check your connection and try again.");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [org?.id, filter, reloadTick, fetchPage]);

  // Issue #5: load more via function that also triggers IntersectionObserver
  async function loadMore() {
    if (!org || !nextCursor || loadingMore) return;
    setLoadingMore(true);
    try {
      const res = await fetchPage(org.id, filter, nextCursor);
      setAllItems((prev) => [...prev, ...res.items]);
      setNextCursor(res.next_cursor);
    } catch (err) {
      setError(err instanceof ApiFail ? err.message : "Couldn't load more.");
    } finally {
      setLoadingMore(false);
    }
  }

  // Client-side filter by search query (Issue #4)
  // Note: GrackleOut doesn't include description in the list endpoint, so we only search title and owner.
  const filteredItems = searchQuery
    ? allItems.filter((g) => {
        const q = searchQuery.toLowerCase();
        return (g.title || "").toLowerCase().includes(q) || (g.owner.name || "").toLowerCase().includes(q);
      })
    : allItems;

  // Client-side sort (Issue #4)
  const sortedItems = [...filteredItems].sort((a, b) => {
    switch (sortBy) {
      case "newest":
        return new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
      case "oldest":
        return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
      case "name-asc":
        return (a.title || "").localeCompare(b.title || "");
      case "name-desc":
        return (b.title || "").localeCompare(a.title || "");
      case "duration-asc":
        return (a.duration_ms ?? 0) - (b.duration_ms ?? 0);
      case "duration-desc":
        return (b.duration_ms ?? 0) - (a.duration_ms ?? 0);
      default:
        return 0;
    }
  });

  // A signed thumbnail URL can expire while the card sits on screen (FR-ST18's windowed
  // signatures). Refetch that one item for a fresh URL rather than leaving a broken image.
  const onThumbnailStale = useCallback((grackleId: string) => {
    api<GrackleDetail>("GET", `/grackles/${grackleId}`)
      .then((detail) => {
        setAllItems((prev) => prev.map((g) => (g.id === grackleId ? { ...g, thumbnail_url: detail.thumbnail_url } : g)));
      })
      .catch(() => {
        // Best-effort — leave the placeholder showing if the refetch also fails.
      });
  }, []);

  const onDeleted = useCallback((grackleId: string) => {
    setAllItems((prev) => prev.filter((g) => g.id !== grackleId));
  }, []);

  // Issue #24: check email verification for record CTA
  const canRecord = user?.email_verified ?? false;
  const mobile = useIsMobileLayout();

  if (sessionLoading) {
    return <PageLoader label="Loading your library…" />;
  }
  if (!user) {
    return <Navigate to="/signin" replace />;
  }
  if (!org) {
    return <Navigate to="/orgs" replace />;
  }

  const processingCount = allItems.filter((g) => g.status === "uploading" || g.status === "processing").length;

  const subtitle = loading
    ? "Loading…"
    : searchQuery
      ? `${countLabel(sortedItems.length, "match")} for "${searchQuery}" — titles and owners of the recordings loaded so far`
      : countLabel(allItems.length);

  const emptyTitle = searchQuery ? "No recordings match your search" : filter === "mine" ? "No recordings yet" : "Nothing here yet";
  const emptyBody = searchQuery
    ? "Try a different search term."
    : filter === "mine"
      ? "Record your screen and send the link — no meeting needed."
      : filter === "shared"
        ? "When someone shares a recording with you, your email or one of your teams, it shows up here."
        : "Nobody in this organization has shared a recording with you yet.";

  return (
    <AppShell org={org} user={user} section={filter === "shared" ? "shared" : "library"} canRecord={canRecord} fullWidth>
      {/* While loading, size for the 8-card skeleton. */}
      <div className="lib-page" style={cardPageStyle(loading ? 8 : sortedItems.length, !!nextCursor)}>
        <div className="lib-header">
          <div className="lib-header-copy">
            <h1 className="lib-header-title">{FILTER_TITLES[filter]}</h1>
            <div className="lib-header-sub">{subtitle}</div>
          </div>
          <SegmentedControl
            className="lib-scope"
            label="Filter recordings"
            options={SCOPE_OPTIONS}
            value={filter}
            onChange={(v) => setFilter(v as LibraryFilter)}
          />
          <Select
            className="lib-sort-select"
            size="sm"
            aria-label="Sort recordings"
            value={sortBy}
            onChange={(v) => setSortBy(v as SortOption)}
            options={Object.entries(SORT_LABELS).map(([value, label]) => ({ value, label }))}
          />
          {/* FR-P14: the desktop topbar carries "Upload"; the mobile shell's tab bar has no room
              for it, so on mobile the library — where uploads end up — is where it starts. */}
          {mobile && canRecord ? (
            <Link to={uploadPath(org.slug)} className="gk-btn gk-btn--secondary gk-btn--sm">
              Upload a file
            </Link>
          ) : null}
        </div>

        {error ? (
          <div className="lib-error">
            <Callout tone="danger" icon="warning-circle">
              <strong>Couldn't load your library.</strong> {error}
            </Callout>
            <Button variant="secondary" onClick={() => setReloadTick((t) => t + 1)}>
              Retry
            </Button>
          </div>
        ) : null}

        {error ? null : loading ? (
          <SkeletonGrid />
        ) : sortedItems.length === 0 ? (
          <EmptyState
            className="lib-empty"
            icon={searchQuery ? "magnifying-glass" : filter === "shared" ? "folder-user" : "video-camera"}
            title={emptyTitle}
            body={emptyBody}
            action={
              !searchQuery && filter === "mine" ? (
                // Record is the ink pill (DS), like the shell's Record a video in the top bar.
                <Link to="/record" className="gk-btn gk-btn--dark">
                  <Icon name="video-camera" size={20} />
                  Record a video
                </Link>
              ) : null
            }
          />
        ) : (
          <>
            {processingCount > 0 ? (
              // The DS LibraryScreen's processing notice: lavender wash, hairline, 12px, with the
              // "{n} processing" badge in place of a Banner icon.
              <div className="lib-processing" role="status">
                <Badge tone="processing" size="sm">
                  {processingCount} processing
                </Badge>
                <span>Still uploading — they stay in the grid and start playing as soon as they're ready.</span>
              </div>
            ) : null}
            <div className="lib-grid">
              {sortedItems.map((g) => (
                <GrackleCard key={g.id} grackle={g} onThumbnailStale={onThumbnailStale} onDeleted={onDeleted} />
              ))}
            </div>

            {/* Loading indicator for infinite scroll */}
            {loadingMore && (
              <div className="lib-loading-indicator">
                <div className="lib-skeleton-line" style={{ width: "60px", margin: "0 auto" }} />
              </div>
            )}

            {/* Intersection observer target */}
            <div ref={observerTarget} style={{ height: 1 }} />

            {!nextCursor && sortedItems.length > 0 && (
              <div className="lib-load-more">
                All&nbsp;<span className="mono">{sortedItems.length}</span>&nbsp;recordings loaded
              </div>
            )}
          </>
        )}
      </div>
    </AppShell>
  );
}
